import React, { Fragment, useRef, useEffect,useState } from "react";
import ReactDOM from "react-dom";
import useSetState from "customHooks/setState";
import useInterval from "customHooks/interval";
import AudioControlsButton from "components/common/AudioControlsButton";
import Icon from "components/Icon";
import WordRadicalContent from "./WordRadicalContent";
import { useStore, StoreTypes } from "context";
import classnames from "classnames";
import styles from "./index.module.scss";
import { ExtendedContentContainer, ExtendedContentType } from "components/ExtendedContent/ExtendedContentContainer";
import * as actionTypes from "constants/actionTypes";
import Repository from "repositories/Repository";
import { ScrollCoordinate, ScrollPositionButtonStateType,ScrollPositionType} from "constants/scrollPositionType";
import { ScrollButtonStateBehavior,scrollPositionButtonBehavior } from "../ExtendedContent/ExtendedContentScrollPositionButton";
const { ExtendedResourceRepository } = Repository;

// 取得部首內容中「不可拆開」的單位位置：.wordBox（部首標題框）、ruby（字＋注音）、圖片，
// 以及不在 ruby／.wordBox 內的純文字（標點、無注音模式），純文字以 Range 取得每一欄的片段。
const getRadicalUnitRects = root => {
  const rects = [];
  root.querySelectorAll(".wordBox, ruby, img").forEach(node => {
    if (node.parentElement && node.parentElement.closest(".wordBox, ruby")) return;
    if (node.tagName === "RUBY") {
      // ruby 的框不一定包含注音 rt（直書時排在字的右側），合併兩者避免注音在頁緣被切掉
      const parts = [node, ...Array.from(node.querySelectorAll("rt"))]
        .map(n => n.getBoundingClientRect())
        .filter(r => r.width > 0 && r.height > 0);
      if (!parts.length) return;
      rects.push({
        left: Math.min(...parts.map(r => r.left)),
        right: Math.max(...parts.map(r => r.right)),
        width: 1,
        height: 1,
      });
      return;
    }
    rects.push(...Array.from(node.getClientRects()));
  });
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  let textNode = walker.nextNode();
  while (textNode) {
    if (textNode.textContent.trim() && !textNode.parentElement.closest(".wordBox, ruby")) {
      range.selectNodeContents(textNode);
      rects.push(...Array.from(range.getClientRects()));
    }
    textNode = walker.nextNode();
  }
  return rects.filter(r => r.width > 0 && r.height > 0);
};

// 依「欄」計算每一頁的起點（距內容起點的捲動距離，px）。
// 內容為直書由右往左排，依序擺放各單位，放不下的那一欄就從新的一頁開始並對齊畫面右緣（NBK-1743）。
// 回傳 { pages, base }：base 為第一頁首欄與畫面右緣的距離，每一頁沿用相同留白。
const computeRadicalPages = el => {
  const elRect = el.getBoundingClientRect();
  // 被 ExtendedPinchPanContainer 放大時 getBoundingClientRect 會跟著縮放，需換算回原尺寸
  const scale = el.offsetWidth ? elRect.width / el.offsetWidth : 1;
  const scrolled = Math.abs(el.scrollLeft);
  const items = getRadicalUnitRects(el)
    .map(r => ({
      start: (elRect.right - r.right) / scale + scrolled,
      end: (elRect.right - r.left) / scale + scrolled,
    }))
    .sort((a, b) => a.start - b.start);
  if (!items.length) return { pages: [0], base: 0 };

  // 第一頁內容與畫面右緣的留白，之後每一頁沿用相同留白
  const base = items[0].start;
  const available = el.clientWidth - base;
  const pages = [0];
  let pageStart = base;
  items.forEach(({ start, end }) => {
    if (end - pageStart > available + 1 && start > pageStart + 1) {
      pageStart = start;
      pages.push(start - base);
    }
  });
  return { pages, base };
};
const WordRadical = ({ data = {} }) => {
  const [{ bookId }] = useStore(StoreTypes.books);
  const [{ audioManager }, globalDispatch] = useStore(StoreTypes.global);
  const { Path, Progress, Content, Audio, OtherSourceUrl } = data;
  const [{ sliderIndex, wordRadicalProgressIndex }, setState] = useSetState({
    sliderIndex: 0,
    wordRadicalProgressIndex: 0,
  });
  const [{ fontSize,scrollPosition }, extendedContentDispatch] = useStore(StoreTypes.extendedContent);
  const pageInfoRef = useRef({ currentPage: -1, totalPages: -1 });

  const [{ isShowRadicalProgressModal }, wordGridDispatch] = useStore(
    StoreTypes.wordGrid
  );
  const contentRef = useRef(null);
  // 每一頁的起點（computeRadicalPages 的結果）與目前所在頁
  const pagesRef = useRef([0]);
  const pageBaseRef = useRef(0);
  const pageIndexRef = useRef(0);

  const maxItemListIndex = Progress.length - 1;
  const [currentScrollLeft, setCurrentScrollLeft] = useState(0);
  const [lastScrollLeft, setLastScrollLeft] = useState(0);

  useInterval(() => {
    let index = sliderIndex + 1;
    if (index <= 0) index = maxItemListIndex;
    if (index > maxItemListIndex) index = 0;
    setState({ sliderIndex: index });
  }, 3000);

  const progressModalHandler = (status) => {
    wordGridDispatch({
      type: actionTypes.SET_RADICAL_PROGRESS_MODAL_SHOW,
      payload: {
        isShowRadicalProgressModal: status,
      },
    });
  };

  const wordRadicalProgressChangeHandler = (index) => {
    setState({
      wordRadicalProgressIndex: Math.max(0, Math.min(index, maxItemListIndex)),
    });
  };

  useEffect(() => {
    // 小視窗內容上下頁分段，因為 none 而 useEffect 的時候才會知道最終移動結果
    switch (scrollPosition) {
      case ScrollPositionType.Next: {
        // console.log("---I類小視窗---WordRadical", contentRef);
        setLastScrollLeft(contentRef.current.scrollLeft);
        goToPage(pageIndexRef.current + 1);
        setCurrentScrollLeft(contentRef.current.scrollLeft);
        break;
      }
      case ScrollPositionType.Prev: {
        setLastScrollLeft(contentRef.current.scrollLeft);
        goToPage(pageIndexRef.current - 1);
        setCurrentScrollLeft(contentRef.current.scrollLeft);
        break;
      }
      default: {
        scrollPositionButtonBehavior({currentScroll:currentScrollLeft, lastScroll: lastScrollLeft, coordinate: ScrollCoordinate.X})
        break;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollPosition]);

  // 分頁完成前先隱藏內容（visibility: hidden 仍佔版面，可正常量測），避免先顯示未分頁的畫面再跳動
  const [isPaginated, setIsPaginated] = useState(false);
  const isPaginatedRef = useRef(false);
  isPaginatedRef.current = isPaginated;
  
  // 進入部首視窗時，若有先前全域正在播放的音檔，需強制停止並清除指標，避免被劫持
  useEffect(() => {
    if (audioManager && audioManager.currentPlayingAudioElement) {
      try {
        audioManager.currentPlayingAudioElement.currentTime = 0;
        audioManager.currentPlayingAudioElement.pause();
        audioManager.currentPlayingAudioElement.dispatchEvent(new CustomEvent('ended', {
          detail: { forced: true }
        }));
      } catch (e) {
        // 忽略停止時可能的例外
      } finally {
        globalDispatch({ type: actionTypes.CLEAR_CURRENT_PLAYING_AUDIO });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 重新計算各頁起點。最後一頁的內容通常不足一整頁寬，瀏覽器捲不到該頁起點，
  // 因此把內容容器（.wordRadicalContent）撐到「最後一頁起點 + 一個畫面寬」，
  // 直書由右往左排，多出的寬度往左延伸，讓最後一頁也能對齊畫面右緣。
  // （不能用 padding：內容是溢出容器的，容器自身的 padding 不會增加可捲動範圍）
  const refreshPages = () => {
    const el = contentRef.current;
    const host = el && el.firstElementChild;
    if (!el || !host) return;
    const { pages, base } = computeRadicalPages(el);
    pagesRef.current = pages;
    pageBaseRef.current = base;
    const minWidth = pages.length > 1 ? `${Math.ceil(pages[pages.length - 1] + el.clientWidth)}px` : "";
    if (host.style.minWidth !== minWidth) host.style.minWidth = minWidth;
  };

  // 以捲動方式換頁時，畫面會顯示可視範圍內的所有東西：
  // 左緣會露出下一頁首欄的一部分、右緣留白處可能露出上一頁的末欄。
  // 因此用 clip-path 遮掉本頁範圍以外的區域，被頁緣切到的欄只會完整出現在它所屬的那一頁。
  // clip-path 作用在捲動容器本身（座標不隨捲動移動），被 PinchPan 放大時會隨之縮放。
  const applyPageClip = () => {
    const el = contentRef.current;
    if (!el) return;
    const pages = pagesRef.current;
    if (pages.length <= 1) {
      el.style.clipPath = "";
      el.style.webkitClipPath = "";
      return;
    }
    const index = pageIndexRef.current;
    const base = pageBaseRef.current;
    const scrolled = Math.abs(el.scrollLeft);
    // 本頁首欄右緣在畫面上的位置（距右緣），其右側屬於上一頁
    const right = Math.max(Math.floor(pages[index] + base - scrolled) - 1, 0);
    // 下一頁首欄右緣在畫面上的位置（距右緣），其左側屬於下一頁
    const left = index < pages.length - 1
      ? Math.max(Math.ceil(el.clientWidth - (pages[index + 1] + base - scrolled)) + 1, 0)
      : 0;
    const clip = `inset(0px ${right}px 0px ${left}px)`;
    el.style.clipPath = clip;
    el.style.webkitClipPath = clip;
  };

  // 捲到指定頁的起點（內容由右往左排，scrollLeft 為 0 或負值）。
  // 換頁前以當下實際版面重新量測分頁，避免字型、圖片晚載入造成的位移；換頁後依頁界裁切。
  const goToPage = index => {
    const el = contentRef.current;
    if (!el) return;
    refreshPages();
    const pages = pagesRef.current;
    const target = Math.max(0, Math.min(index, pages.length - 1));
    pageIndexRef.current = target;
    el.scrollLeft = -pages[target];
    applyPageClip();
  };

  // 依目前捲動位置找出所在頁，同步「目前頁/總頁數」到 store，
  // 供工具列 ExtendedContentScrollPositionButton 顯示，與其他分頁小視窗一致。
  const syncPageInfo = () => {
    if (!contentRef.current) return;
    const pages = pagesRef.current;
    const scrolled = Math.abs(contentRef.current.scrollLeft);
    let nearest = 0;
    pages.forEach((offset, index) => {
      if (Math.abs(offset - scrolled) < Math.abs(pages[nearest] - scrolled)) nearest = index;
    });
    pageIndexRef.current = nearest;
    const totalPages = pages.length;
    const currentPage = nearest;

    if (pageInfoRef.current.totalPages !== totalPages) {
      extendedContentDispatch({ type: actionTypes.SET_EXTENDED_CONTENT_TOTAL_PAGES, payload: { totalPages } });
    }
    if (pageInfoRef.current.currentPage !== currentPage) {
      extendedContentDispatch({ type: actionTypes.SET_EXTENDED_CONTENT_CURRENT_PAGE, payload: { currentPage } });
    }
    pageInfoRef.current = { currentPage, totalPages };
  };

  const updateScrollButtonState = () => {
    if (!contentRef.current) return;
    syncPageInfo()
    const totalPages = pagesRef.current.length
    const pageIndex = pageIndexRef.current
    if (totalPages <= 1) {
      ScrollButtonStateBehavior(ScrollPositionButtonStateType.None)
    } else if (pageIndex === 0) {
      ScrollButtonStateBehavior(ScrollPositionButtonStateType.Head)
    } else if (pageIndex === totalPages - 1) {
      ScrollButtonStateBehavior(ScrollPositionButtonStateType.Bottom)
    } else {
      ScrollButtonStateBehavior(ScrollPositionButtonStateType.Middle)
    }
  }

  // 分頁（goToPage 內會重新量測），停在原本所在頁（超出時停在最後一頁），完成後才顯示內容
  const paginate = () => {
    goToPage(pageIndexRef.current)
    updateScrollButtonState()
    setIsPaginated(true)
  }

  // 內容（HTML、圖片、字型）就緒後的下一個 frame 立即分頁。
  // 原本固定延遲 1 秒才量測，期間會先顯示未分頁的內容，分頁後畫面跳動（NBK-1743）
  const handleContentReady = () => {
    requestAnimationFrame(paginate)
  }

  // 保險：若一直沒收到就緒通知（例如內容載入失敗），3 秒後仍分頁並顯示，避免內容永遠隱藏
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isPaginatedRef.current) paginate()
    }, 3000)
    return () => clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 字級變動後版面已更新，下一個 frame 重新分頁
  const isFirstFontSizeRef = useRef(true)
  useEffect(() => {
    if (isFirstFontSizeRef.current) {
      isFirstFontSizeRef.current = false
      return
    }
    if (!isPaginatedRef.current) return
    const frame = requestAnimationFrame(paginate)
    return () => cancelAnimationFrame(frame)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontSize])

  // 原本的 removeEventListener 寫在 setTimeout callback 的 return 裡，React 拿不到、永遠不會移除，
  // 改為掛載時綁定、由 effect 的 cleanup 移除（NBK-1743）
  useEffect(()=> {
    const el = contentRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollButtonState, false)
    return () => {
      el.removeEventListener('scroll', updateScrollButtonState, false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  // 演變過程視窗本身也是 ExtendedContentContainer，關閉時工具列會把共用 store 的頁碼重設為 1/1，
  // 但部首的捲動位置沒變，需重新同步頁碼與按鈕狀態
  const prevProgressModalShowRef = useRef(isShowRadicalProgressModal);
  useEffect(() => {
    const wasShown = prevProgressModalShowRef.current;
    prevProgressModalShowRef.current = isShowRadicalProgressModal;
    if (!wasShown || isShowRadicalProgressModal) return;
    pageInfoRef.current = { currentPage: -1, totalPages: -1 };
    updateScrollButtonState();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowRadicalProgressModal]);

  return (
    <div className={styles.wordRadical}>
      <div className={styles.mainContent}>
        <div className={styles.right}>
          {/* NBK-1743：喇叭移至部首標題框下方（見 WordRadicalContent 的 belowRadical），此處僅保留影片連結 */}
          <div className={styles.audio}>
            {OtherSourceUrl &&
              OtherSourceUrl.map((item) => (
                <a
                  className={styles[item.type]}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div
                    className={styles.videoIcon}
                    style={{ backgroundImage: "url(assets/img/video.svg)" }}
                  />
                </a>
              ))}
          </div>
        </div>
        <div
          className={classnames(styles.content, fontSize, {
            [styles.noprogress]: Progress.length === 0,
          })}
          ref={contentRef}
          style={{ visibility: isPaginated ? "visible" : "hidden" }}
        >
          <WordRadicalContent
            htmlPath={Content}
            imgPath={Path}
            onReady={handleContentReady}
            belowRadical={Audio && (
              <AudioControlsButton
                src={ExtendedResourceRepository.getDataSrc({
                  bookId,
                  pathName: Audio,
                })}
              />
            )}
          />
        </div>
        {Progress.length > 0 && (
          <div className={styles.progress}>
            <div
              className={classnames(styles.progressSlider, {
                [styles.pointer]: Progress.length > 1,
              })}
            >
              <div className={styles.slider}>
                {Progress.map((item, index) => (
                  <img
                    key={item.Path}
                    src={ExtendedResourceRepository.getDataSrc({
                      bookId,
                      pathName: item.Path,
                    })}
                    alt=""
                    onClick={() => progressModalHandler(Progress.length > 1)}
                    className={classnames(styles.img, {
                      [styles.active]: index === sliderIndex,
                    })}
                  />
                ))}
              </div>
              {Progress.length > 1 && (
                <span
                  className={styles.icon}
                  onClick={() => progressModalHandler(true)}
                >
                  <Icon type="text" name="searchPlus" />
                </span>
              )}
            </div>
          </div>
        )}
      </div>
      {/* NBK-1743：部首被 ExtendedPinchPanContainer 包住後，祖先元素帶有 CSS transform，
          position: fixed 會改為相對該容器定位（只蓋住內容區、且跟著縮放）。
          因此演變過程視窗以 portal 渲染到 body，維持全螢幕。 */}
      {isShowRadicalProgressModal && ReactDOM.createPortal(
        <div className={styles.progressModal}>
          <ExtendedContentContainer
            // NBK-1743：指定類型，讓演變過程視窗可用滾輪與工具列放大（不顯示上下頁）
            extendedContentType={ExtendedContentType.WordRadicalProgress}
            onClose={() => progressModalHandler(false)}
          >
            <div className={styles.progressModalContent}>
              <div className={styles.itemList}>
                {Progress.map((item, index) => (
                  <Fragment key={item.Path}>
                    <div
                      className={classnames(styles.item, {
                        [styles.show]: index <= wordRadicalProgressIndex,
                      })}
                    >
                      <img
                        src={ExtendedResourceRepository.getDataSrc({
                          bookId,
                          pathName: item.Path,
                        })}
                        alt=""
                      />
                      <div className={styles.info}>{item.Info}</div>
                    </div>
                    {index !== maxItemListIndex && (
                      <div
                        className={classnames(styles.icon, {
                          [styles.show]: index <= wordRadicalProgressIndex,
                        })}
                      >
                        <Icon type="text" name="angleRight" />
                      </div>
                    )}
                  </Fragment>
                ))}
              </div>
              <div className={styles.controls}>
                <div className={styles.center}>
                  <span
                    onClick={() =>
                      wordRadicalProgressChangeHandler(
                        wordRadicalProgressIndex - 1
                      )
                    }
                  >
                    <Icon type="text" name="angleLeft" />
                  </span>
                  <span
                    onClick={() =>
                      wordRadicalProgressChangeHandler(
                        wordRadicalProgressIndex + 1
                      )
                    }
                  >
                    <Icon type="text" name="angleRight" />
                  </span>
                </div>
              </div>
            </div>
          </ExtendedContentContainer>
        </div>,
        document.body
      )}
    </div>
  );
};

export default WordRadical;
